import { AuthService } from '@/services/AuthService';
import { ChatService } from '@/services/ChatService';
import { GuestService } from '@/services/GuestService';
import { DocumentsService } from '@/services/DocumentsService';
import { SharedLinkService } from '@/services/SharedLinkService';
import { PasswordResetService } from '@/services/PasswordResetService';
import { FeatureFlagsService } from '@/services/FeatureFlagsService';
import { TokenStore } from '@/infrastructure/storage/TokenStore';
import { ChatStorage } from '@/infrastructure/storage/ChatStorage';
import { sessionManager } from '@/services/sessionManagerInstance';

export class ServiceLocator {
  // cache singletons to avoid multiple instances/intervals/etc.
  private static _tokenStore: TokenStore | null = null;
  private static _authService: AuthService | null = null;
  private static _chatService: ChatService | null = null;
  private static _documentsService: DocumentsService | null = null;
  private static _featureFlagsService: FeatureFlagsService | null = null;
  private static _sharedLinkService: SharedLinkService | null = null;
  private static _chatStorage: ChatStorage | null = null;

  static getTokenStore(): TokenStore {
    if (!this._tokenStore) this._tokenStore = new TokenStore();
    return this._tokenStore;
  }

  static getChatStorage(): ChatStorage {
    if (!this._chatStorage) this._chatStorage = new ChatStorage();
    return this._chatStorage;
  }

  static getAuthService(): AuthService {
    if (!this._authService) {
      this._authService = new AuthService(
        this.getApiBaseUrl(),
        this.getTokenStore()
      );
    }
    return this._authService;
  }

  static getPasswordResetService(): PasswordResetService {
    return new PasswordResetService(this.getApiBaseUrl());
  }

  static getChatService(): ChatService {
    if (!this._chatService) {
      this._chatService = new ChatService(
        this.getApiBaseUrl(),
        this.getTokenStore()
      );
    }
    return this._chatService;
  }

  static getGuestService(): GuestService {
    return new GuestService(this.getApiBaseUrl());
  }

  static getDocumentsService(): DocumentsService {
    if (!this._documentsService) {
      this._documentsService = new DocumentsService(
        this.getApiBaseUrl(),
        this.getTokenStore()
      );
    }
    return this._documentsService;
  }

  static getSharedLinkService(): SharedLinkService {
    if (!this._sharedLinkService) {
      this._sharedLinkService = new SharedLinkService(
        this.getApiBaseUrl(),
        this.getTokenStore()
      );
    }
    return this._sharedLinkService;
  }

  private static getApiBaseUrl(): string {
    const baseUrl = import.meta.env.VITE_API_BASE_URL;
    if (!baseUrl) {
      throw new Error('API_BASE_URL is not set');
    }
    return baseUrl;
  }

  static getFeatureFlagsService(): FeatureFlagsService {
    if (!this._featureFlagsService) {
      this._featureFlagsService = new FeatureFlagsService(
        this.getApiBaseUrl(),
        this.getTokenStore()
      );
    }
    return this._featureFlagsService;
  }

  static getSessionManager() {
    return sessionManager;
  }
}
